import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService, NoActiveAccountError } from 'app/core/auth/auth.service';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { ApiConfiguration } from '../../api/api-configuration';
import { debugLog } from 'app/shared/utils/debug-logger';
/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const _authService = inject(AuthService);
  const apiConfiguration = inject(ApiConfiguration);
  const router = inject(Router);

  // If this is a request to a public resource, pass it through without authentication
  if (isPublicRequest(req, apiConfiguration.rootUrl)) {
    return next(req);
  }

  // For non-public requests, add the token as before
  return _authService.ensureAuthToken().pipe(
    switchMap(token => {
      if (!token) {
        // If we couldn't get a token, redirect to login
        console.error('Authentication required');
        _authService.signOut();
        router.navigate(['/home']);
        return throwError(() => new Error('Authentication required'));
      }

      const authReq = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${token}`),
      });

      return next(authReq).pipe(
        catchError(error => {
          if (error.status === 401) {
            // If we get a 401, try to refresh the token once more
            return _authService.refreshToken().pipe(
              switchMap(newToken => {
                const retryReq = req.clone({
                  headers: req.headers.set('Authorization', `Bearer ${newToken}`),
                });
                return next(retryReq);
              }),
              catchError(refreshError => {
                _authService.signOut();
                router.navigate(['/home']);
                return throwError(() => refreshError);
              })
            );
          }
          return throwError(() => error);
        })
      );
    }),
    catchError(tokenError => {
      // Don't redirect immediately on token errors - let the auth guards handle routing
      if (NoActiveAccountError.is(tokenError)) {
        debugLog('auth', 'Unauthenticated request blocked by interceptor:', req.url);
      } else {
        console.warn('Token acquisition failed in interceptor:', tokenError);
      }
      return throwError(() => tokenError);
    })
  );
};

// Helper function to determine if a request is for a public resource
function isPublicRequest(req: HttpRequest<unknown>, baseUrl: string): boolean {
  const publicEndpoints = [];
  // Check if the request URL includes public endpoints
  if (req.method === 'GET') {
    publicEndpoints.push('/settings');
  }

  const publicWildCards = ['/public/', 'assets/icons/'];

  return (
    publicEndpoints.some(endpoint => req.url === baseUrl + endpoint) ||
    publicWildCards.some(endpoint => req.url.includes(endpoint))
  );
}
